import React, { useState } from "react";
import { Download, FileText, Server, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import MD from "./Markdown";

const FALLBACK_DOWNLOADS = [
  { name: "Mirror Europa",  url: "#", size: "5.8 GB", type: "mirror", description: "Servidor Europa - 100 Mbps" },
  { name: "Mirror América", url: "#", size: "5.8 GB", type: "mirror", description: "Servidor US - 100 Mbps" },
  { name: "Torrent",        url: "#", size: "5.8 GB", type: "torrent", description: "P2P - Velocidad ilimitada" },
];

const REQUIREMENTS = {
  min: { os: "Windows 7 / 10 / 11", cpu: "Intel i3 / Ryzen 3", ram: "4 GB", gpu: "GTX 750 / RX 460", disk: "15 GB" },
  rec: { os: "Windows 10 / 11", cpu: "Intel i5 / Ryzen 5", ram: "8 GB", gpu: "GTX 1060 / RX 580", disk: "15 GB SSD" },
};

const DownloadSection = () => {
  const { value: intro } = useContent("downloads.intro", "");
  const { value: list } = useContent("downloads.list", FALLBACK_DOWNLOADS);
  const items = Array.isArray(list) && list.length ? list : FALLBACK_DOWNLOADS;

  const handleClick = (item) => {
    if (item.url && item.url !== "#") {
      window.open(item.url, "_blank", "noopener");
      toast.success(`Iniciando descarga · ${item.name}`);
    } else {
      toast.error("URL no configurada", { description: "Pedile al admin que configure el link." });
    }
  };

  return (
    <section className="relative py-28 bg-[#0a0a0d] overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1585588985526-9459c1bb9e9e?auto=format&fit=crop&w=1920&q=70)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center mb-14">
          <div className="ornament"><span>Descarga el cliente</span></div>
          <h2 className="section-title mt-6">Únete a la Batalla</h2>
          <div className="gold-divider w-40 mt-6" />
          {intro ? (
            <div className="mt-6 max-w-2xl text-left"><MD>{intro}</MD></div>
          ) : (
            <p className="mt-6 max-w-2xl text-[#b8b3a8]">
              Descarga el cliente Interlude completo. Instalación automática, sin patches manuales.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-12">
          {items.map((m, i) => (
            <div key={(m.url || m.name) + i} className="glass rounded-sm p-6 hover:translate-y-[-3px] transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.3)] flex items-center justify-center">
                    <Server className="w-5 h-5 text-[#d4a843]" />
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-[#f0ebe0]">{m.name}</h4>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#8a8a92]">{(m.type || "mirror").toUpperCase()}{m.size ? ` · ${m.size}` : ""}</span>
                  </div>
                </div>
              </div>
              {m.description && (
                <p className="text-xs text-[#b8b3a8] mb-3">{m.description}</p>
              )}
              <button
                onClick={() => handleClick(m)}
                className="btn-gold w-full py-3 text-xs rounded-sm flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" /> Descargar
              </button>
            </div>
          ))}
          <div className="glass rounded-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.3)] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <h4 className="font-display font-bold text-[#f0ebe0]">100% Seguro</h4>
            </div>
            <p className="text-xs text-[#b8b3a8] mb-3">Verificado y libre de malware. SHA-256 disponible.</p>
            <code className="block text-[10px] font-mono text-[#6f6c66] truncate bg-[#14141a] p-2 rounded-sm border border-[rgba(212,168,67,0.1)]">
              a3f5...8c12d4ef...90b3
            </code>
          </div>
        </div>

        {/* Requirements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { title: "Mínimos", data: REQUIREMENTS.min, color: "#b8b3a8" },
            { title: "Recomendados", data: REQUIREMENTS.rec, color: "#d4a843" },
          ].map((r) => (
            <div key={r.title} className="glass rounded-sm p-7">
              <div className="flex items-center gap-2 mb-5">
                <FileText className="w-4 h-4" style={{ color: r.color }} />
                <h3 className="font-display font-bold tracking-[0.18em] uppercase text-sm" style={{ color: r.color }}>
                  Requisitos {r.title}
                </h3>
              </div>
              <dl className="space-y-2.5">
                {Object.entries(r.data).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-[rgba(212,168,67,0.08)] pb-2.5 text-sm">
                    <dt className="font-mono uppercase text-[10px] tracking-[0.25em] text-[#8a8a92]">{k}</dt>
                    <dd className="text-[#f0ebe0]">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;
